/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { TMyHubActionType } from "../../../model/types";
import type { TCarouselItem, TContentType } from "../../../model/types";
import { Locale, LocaleKeys } from "../../../model/locale";
import { formatDuration } from "../../contentDetail/contentDetailMappers";
import { formatSavedDate, interpolate } from "../myHubSavedMappers";
import { composeSeriesAiringLabel } from "../myHubSeriesMappers";
import type {
  TMyHubRecordingHighlightItem,
  TRecordingHighlightCardType
} from "./recordingHighlight.types";

/**
 * The item's actions, in the service's order.
 */
const toRenderableActions = (
  availableActions: readonly TMyHubActionType[] | undefined
): TMyHubActionType[] =>
  availableActions === undefined ? [] : [...availableActions];

/**
 * The series card's content-type label per `TContentType` — Figma's
 * "TV Series" for an episode. Only the types a My Hub recording can actually
 * be are mapped (see `mapProgramTypeToContentType` in myHubMappers.ts); a
 * movie's card shows no type, and anything else (e.g. `sports`) is left
 * unlabelled rather than guessed at.
 */
const TYPE_LABEL_KEYS: Partial<Record<TContentType, string>> = {
  episode: LocaleKeys.CONTENT_TYPE_LABEL_SERIES
};

/**
 * Which info card a tile takes — the movie card for a movie, the series card
 * for everything else.
 */
export const toRecordingHighlightCardType = (
  tile: TCarouselItem
): TRecordingHighlightCardType =>
  tile.contentType === "movie" ? "movie" : "series";

/**
 * "S5:E14 - Long Episode Title Here" — the series card's episode line,
 * separate from the series title `title` already carries. `""` for a movie,
 * or any tile missing either half of the season/episode pair.
 */
const buildEpisodeLabel = (tile: TCarouselItem): string => {
  if (tile.seasonNumber === undefined || tile.episodeNumber === undefined) {
    return "";
  }
  const parts: string[] = [
    "S" + String(tile.seasonNumber) + ":E" + String(tile.episodeNumber)
  ];
  if (tile.episodeTitle !== undefined && tile.episodeTitle !== "") {
    parts.push("- " + tile.episodeTitle);
  }
  return parts.join(" ");
};

/**
 * "First aired 6/10/2026" / "First airs 6/10/2026" — the tile's own airing
 * time, not the series' premiere date. Future vs past mirrors
 * `PreviewFormatter.getFirstAiredText()`, which reads the same distinction
 * for the live-preview surfaces; `""` when the tile carries no `startTime`.
 */
const buildFirstAiredLabel = (startTime: string | undefined): string => {
  const date: string = formatSavedDate(startTime);
  if (date === "") {
    return "";
  }
  const isFutureAiring: boolean =
    new Date(startTime as string).getTime() > Date.now();
  const prefix: string = Locale.getString(
    isFutureAiring ? LocaleKeys.FIRST_AIRS : LocaleKeys.FIRST_AIRED
  );
  return prefix + " " + date;
};

/**
 * The movie card's airing line — "Today  9:00-10:00pm on NBC" — composed
 * the way the series lists compose theirs. `""` for the series card, which
 * shows a first-aired date instead.
 *
 * @param tile - The tile the popup was opened from.
 * @param channelName - The station's name, or `""` while it is unresolved.
 * @returns The line, or `""`.
 */
export const buildRecordingHighlightScheduleLabel = (
  tile: TCarouselItem,
  channelName: string = ""
): string =>
  toRecordingHighlightCardType(tile) === "movie"
    ? composeSeriesAiringLabel(tile, channelName)
    : "";

/**
 * Builds the popup's item from the My Hub tile the user selected.
 *
 * The tile is carried through whole as well as read from: an action acts on
 * the tile (playback hands it to the shared tile helper), while the strings
 * below are what the popup renders.
 *
 * @param tile - The mapped My Hub tile the popup was opened from.
 * @returns The item to hand {@link showRecordingHighlight}.
 */
export const toRecordingHighlightItem = (
  tile: TCarouselItem
): TMyHubRecordingHighlightItem => {
  const expiresOnDate: string = formatSavedDate(tile.expiresOn);
  const typeLabelKey: string | undefined = TYPE_LABEL_KEYS[tile.contentType];

  return {
    id: tile.id,
    // `id` above is the myHubItemId; the recordings service takes this one.
    recordingId: tile.recordingId,
    contentId: tile.contentId,
    availableActions: toRenderableActions(tile.availableActions),
    tile,
    cardType: toRecordingHighlightCardType(tile),
    title: tile.title,
    // A scheduled airing has recorded nothing yet, so it carries no expiry and
    // the line is dropped rather than printed empty.
    expiresOnLabel:
      expiresOnDate === ""
        ? ""
        : interpolate(
            Locale.getString(LocaleKeys.MY_HUB_SAVED_EXPIRES_ON),
            expiresOnDate
          ),
    backdropImageUrl: tile.backdropImageUrl || "",
    episodeLabel: buildEpisodeLabel(tile),
    typeLabel: typeLabelKey === undefined ? "" : Locale.getString(typeLabelKey),
    durationLabel: formatDuration(tile.duration) || "",
    firstAiredLabel: buildFirstAiredLabel(tile.startTime),
    // Without the channel clause until the station's name resolves — see
    // `openRecordingHighlight`.
    scheduleLabel: buildRecordingHighlightScheduleLabel(tile)
  };
};
